import { FC, useRef } from 'react';

import { SPACE } from 'utils/constants/common';

import { Text, TextTypography, TooltipHover } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import {
    CAMPAIGNS_TABLE_COLUMNS_MAP,
    CampaignsTableColumn,
} from 'components/CampaignsDashboard/components/CampaignsTable/columns';
import { MobileEllipsisText } from 'components/CampaignsDashboard/components/CampaignsTable/components/MobileEllipsisText';
import { CELL_TYPOGRAPHY, MOBILE_CELL_TYPOGRAPHY } from 'components/CampaignsDashboard/utils/constants';

import { CampaignAreas as CampaignAreasType } from 'store/models/campaignsDashboard';
import { selectCampaignRegions } from 'store/models/createCampaign';
import { useSelector } from 'store/utils';

const TrlKeys = {
    excluded: 'CampaignsDashboard.CampaignsTable.CampaignAreas.excluded',
};

interface CampaignAreasProps {
    areas: CampaignAreasType;
    isMobile?: boolean;
    typography?: TextTypography;
}

export const CampaignAreas: FC<CampaignAreasProps> = ({ areas, isMobile = false, typography: typographyOverride }) => {
    const regions = useSelector((state) => selectCampaignRegions(state));

    const trls = useTrls();
    const activatorRef = useRef<HTMLDivElement>(null);
    const typography = typographyOverride ?? (isMobile ? MOBILE_CELL_TYPOGRAPHY : CELL_TYPOGRAPHY);

    const includedAreas = areas.include.map((id) => regions[id]);
    const excludedAreas = areas.exclude.map((id) => regions[id]);
    const contentTypography = isMobile ? 'paragraph-1-regular' : typography;
    const includedAreasText = includedAreas.join(', ');
    const excludedAreasText = excludedAreas.join(', ');
    const excludedLabel = trls(TrlKeys.excluded);
    const mobileContent = [
        includedAreasText,
        excludedAreas.length > 0 ? `${excludedLabel}${SPACE}${excludedAreasText}` : '',
    ]
        .filter(Boolean)
        .join(SPACE);

    const content = (
        <>
            {includedAreasText}
            <br />
            {excludedAreas.length > 0 && (
                <>
                    <Text Element="span" typography={contentTypography} style="secondary">
                        {excludedLabel}
                    </Text>
                    {SPACE}
                    <Text Element="span" typography={contentTypography}>
                        {excludedAreasText}
                    </Text>
                </>
            )}
        </>
    );

    if (isMobile) {
        return (
            <MobileEllipsisText
                typography={typography}
                bottomSheetTitle={trls(CAMPAIGNS_TABLE_COLUMNS_MAP[CampaignsTableColumn.Areas].trlKey)}
                bottomSheetContent={content}
                dataQa="campaign-areas-full-content"
            >
                {mobileContent}
            </MobileEllipsisText>
        );
    }

    return (
        <>
            <Text maxLines={3} typography={typography}>
                <span ref={activatorRef}>{content}</span>
            </Text>
            <TooltipHover maxWidth={600} delay={1000} activatorRef={activatorRef} placement="top-center" forcePlacement>
                {content}
            </TooltipHover>
        </>
    );
};
